import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { getSnapshotItemProperties } from '@/backend/darwin/teamProperties'
import { useClasses } from '@/modules/Classes/useClasses'
import { useTeamProperties } from '@/modules/Classes/useTeamProperties'
import { useTeamProperty } from '@/modules/Classes/useTeamProperty'
import { useCurrentItemStore } from '@/modules/Workview/useCurrentItemStore'
import { useWorkflowInWorkviewStore } from '@/modules/Workview/useWorkflowInWorkviewStore'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useToast } from '@/uiKit/Toast/useToast'

import useIsTimeTravelling from './useIsTimeTravelling'

type View =
  | { itemId: string; kind: 'live' }
  | { itemId: string; kind: 'snapshot'; snapshotId: string }
  | { itemId: string; kind: 'empty' }

type Load = { key: string; promise: Promise<void> }

/**
 * Owns what the workview shows for the selected stage visit of the current item: the live item
 * property values, or what a past stage's exit snapshot recorded (item values, the property
 * definitions they need, deleted ones included, and the classes deleted since).
 *
 * Every change of the selected view starts a new generation and drops the previous view's data at
 * once. A response, item values or annotations alike, is applied only while its generation is
 * current.
 *
 * Catalog rule: while time travelling, the snapshot's property definitions and classes win over
 * the live ones with the same id; on the current stage nothing is overlaid, so live wins.
 */
export const useStageView = defineStore('stageView', () => {
  const currentItemStore = useCurrentItemStore()
  const workflowStore = useWorkflowInWorkviewStore()
  const teamStore = useTeamStore()
  const teamProperties = useTeamProperties()
  const classesStore = useClasses()
  const teamProperty = useTeamProperty()
  const isTimeTravelling = useIsTimeTravelling()
  const toast = useToast()

  const view = computed<View | null>(() => {
    const itemId = currentItemStore.currentItemId
    if (!itemId) {
      return null
    }
    // like the annotation loader, an item with no current stage shows its latest state
    if (!isTimeTravelling.value || workflowStore.itemState?.current_stage_instances.length === 0) {
      return { itemId, kind: 'live' }
    }
    const selected = workflowStore.selectedStageInstance
    if (!selected) {
      // the following stage is selected without an instance and shows nothing; any other stage
      // without one is still being selected
      const stageId = workflowStore.selectedStage?.id
      return stageId && stageId === workflowStore.followingStage?.id
        ? { itemId, kind: 'empty' }
        : null
    }
    const snapshotId = selected.data.exit_snapshot_id
    return snapshotId ? { itemId, kind: 'snapshot', snapshotId } : { itemId, kind: 'empty' }
  })

  /** What annotation loaders load: live, nothing, a snapshot, or nothing yet while not ready */
  const selectedSnapshotId = computed<'current' | 'empty' | string | null>(() => {
    if (!view.value) {
      return null
    }
    if (view.value.kind === 'snapshot') {
      return view.value.snapshotId
    }
    return view.value.kind === 'live' ? 'current' : 'empty'
  })

  const generation = ref(0)
  let load: Load | null = null

  const startGeneration = (keepItemValues: boolean): void => {
    generation.value++
    load = null
    if (!keepItemValues) {
      teamProperties.currentItemProperties = []
    }
    teamProperties.setSnapshotDefinitions([])
    classesStore.setSnapshotClasses([])
  }

  const viewKey = (selected: View | null): string =>
    selected
      ? `${selected.itemId}/${selected.kind === 'snapshot' ? selected.snapshotId : selected.kind}`
      : ''

  // the last view shown; a view that is not ready yet keeps it, so loads started meanwhile stand
  let shown = view.value

  /** Drops everything shown, e.g. when the workview closes */
  const reset = (): void => {
    startGeneration(false)
    // the view selected now is what the next generation shows; leaving it starts another one
    shown = view.value
  }

  watch(
    () => viewKey(view.value),
    (key) => {
      const next = view.value
      if (!next) {
        // no item open, or another item whose stage is not selected yet
        if (shown && shown.itemId !== currentItemStore.currentItemId) {
          reset()
        }
        return
      }
      if (key === viewKey(shown)) {
        return
      }
      // a first live view keeps loads started before the stage was selected; any other view
      // must not receive them
      if (shown || next.kind !== 'live') {
        // live values of the previous live view stay on screen until the new ones arrive
        startGeneration(next.kind === 'live' && shown?.kind === 'live')
      }
      shown = next
    },
    { flush: 'sync' },
  )

  const fetchLive = async (itemId: string, current: () => boolean): Promise<void> => {
    const stageInstance = workflowStore.assignedStageInstance || workflowStore.selectedStageInstance
    let properties: typeof teamProperties.currentItemProperties = []
    try {
      const response = await teamProperty.getItemPropertiesForItem(
        itemId,
        stageInstance || undefined,
      )
      properties = response?.properties ?? []
    } catch {
      if (current()) {
        toast.error({ meta: { title: 'Failed to load item properties' } })
      }
    }
    if (current()) {
      teamProperties.currentItemProperties = properties
    }
  }

  const fetchSnapshot = async (
    itemId: string,
    snapshotId: string,
    current: () => boolean,
  ): Promise<void> => {
    const teamSlug = teamStore.currentTeam?.slug
    if (!teamSlug) {
      return
    }
    try {
      const { data } = await getSnapshotItemProperties(teamSlug, itemId, snapshotId)
      if (current()) {
        teamProperties.setSnapshotDefinitions(data.property_definitions)
        classesStore.setSnapshotClasses(data.annotation_classes)
        teamProperties.currentItemProperties = data.properties
      }
    } catch {
      if (current()) {
        toast.error({ meta: { title: 'Failed to load properties for this stage' } })
      }
    }
  }

  const start = (): Promise<void> => {
    const selected = view.value
    // a snapshot is fetched once the selection is among the item's own stage instances; until
    // then nothing is cached, and the item's own selection loads it
    const state = selected && workflowStore.itemStates[selected.itemId]
    const selectedId = workflowStore.selectedStageInstance?.id
    const ownSelection =
      !!state &&
      [...state.current_stage_instances, ...state.previous_stage_instances].some(
        (instance) => instance.id === selectedId,
      )
    if (selected?.kind === 'snapshot' && !ownSelection) {
      return Promise.resolve()
    }
    const started: Load = { key: viewKey(selected), promise: Promise.resolve() }
    const current = (): boolean => load === started
    if (selected?.kind === 'live') {
      started.promise = fetchLive(selected.itemId, current)
    } else if (selected?.kind === 'snapshot') {
      started.promise = fetchSnapshot(selected.itemId, selected.snapshotId, current)
    }
    load = started
    return started.promise
  }

  /** Resolves once the selected view's data was fetched; one fetch per generation */
  const ready = (): Promise<void> => (load?.key === viewKey(view.value) ? load.promise : start())

  /** Loads the selected view; a live view refetches its item values, they may have changed */
  const show = (): Promise<void> => (view.value?.kind === 'live' ? start() : ready())

  /** New generation for the same view, e.g. once an archived snapshot is restored */
  const refresh = (): Promise<void> => {
    startGeneration(false)
    return ready()
  }

  return {
    selectedSnapshotId,
    generation,
    ready,
    show,
    refresh,
    reset,
  }
})
